import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Eye,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Trash2,
  Users,
  BadgeAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import { differenceInDays, parseISO } from 'date-fns';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { extractErrorMessage } from '@/lib/api-client';
import { formatDate, initialsFromName } from '@/lib/format';
import { useDeleteDriver, useDriversList } from './api';
import { DriverFormDialog } from './driver-form-dialog';
import type { Driver } from './types';

function cnhStatus(iso: string): {
  label: string;
  variant: 'success' | 'warning' | 'destructive';
} {
  const days = differenceInDays(parseISO(iso), new Date());
  if (days < 0) return { label: 'Vencida', variant: 'destructive' };
  if (days <= 30) return { label: `Vence em ${days}d`, variant: 'warning' };
  return { label: `Válida`, variant: 'success' };
}

export function DriversPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Driver | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Driver | null>(null);

  const debouncedSearch = useDebouncedValue(search, 300);
  const query = useDriversList({
    page,
    limit: 20,
    q: debouncedSearch || undefined,
    sort: 'name',
  });
  const deleteMutation = useDeleteDriver();

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (d: Driver) => {
    setEditing(d);
    setFormOpen(true);
  };
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success(`${deleteTarget.name} removido`);
      setDeleteTarget(null);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const total = query.data?.meta.total ?? 0;
  const items = query.data?.data ?? [];

  return (
    <div className="page">
      <header className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="space-y-1">
          <div className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            <Users className="size-3.5" />
            Frota
          </div>
          <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">Motoristas</h1>
          <p className="text-sm text-muted-foreground">
            Cadastro de motoristas, CNH, contato e endereço.
          </p>
        </div>
        <Button onClick={openCreate} size="lg">
          <Plus className="size-4" />
          Novo motorista
        </Button>
      </header>

      <Card>
        <CardContent className="flex items-center gap-3 p-4">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Buscar por nome, CPF ou CNH…"
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Motorista</th>
                  <th className="px-4 py-3 font-medium">CPF</th>
                  <th className="px-4 py-3 font-medium">CNH</th>
                  <th className="px-4 py-3 font-medium">Validade</th>
                  <th className="px-4 py-3 font-medium">Pontos</th>
                  <th className="w-10 px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {query.isLoading &&
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 6 }).map((_, j) => (
                        <td key={j} className="px-4 py-3">
                          <Skeleton className="h-4 w-full" />
                        </td>
                      ))}
                    </tr>
                  ))}

                {!query.isLoading && items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center">
                      <div className="mx-auto flex max-w-sm flex-col items-center gap-3">
                        <div className="flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                          <Users className="size-6" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">Nenhum motorista ainda</p>
                          <p className="text-xs text-muted-foreground">
                            {debouncedSearch
                              ? 'Nada encontrado com essa busca.'
                              : 'Cadastre o primeiro motorista da sua operação.'}
                          </p>
                        </div>
                        {!debouncedSearch && (
                          <Button onClick={openCreate} size="sm">
                            <Plus className="size-4" />
                            Cadastrar motorista
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}

                {items.map((d) => {
                  const status = cnhStatus(d.cnhExpiresAt);
                  return (
                    <tr
                      key={d.id}
                      className="group cursor-pointer transition-colors hover:bg-muted/30"
                      onClick={() => navigate(`/app/drivers/${d.id}`)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar className="size-9">
                            <AvatarFallback>{initialsFromName(d.name)}</AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <p className="truncate font-medium">{d.name}</p>
                            <p className="truncate text-xs text-muted-foreground">
                              {d.phone ?? d.email ?? '—'}
                              {d.city && d.state ? ` · ${d.city}/${d.state}` : ''}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs tabular-nums text-muted-foreground">
                          {d.cpf}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col leading-tight">
                          <span className="font-mono text-xs tabular-nums">{d.cnh}</span>
                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                            Categoria {d.cnhCategory}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col items-start gap-1 leading-tight">
                          <span className="text-xs text-muted-foreground">
                            {formatDate(d.cnhExpiresAt)}
                          </span>
                          <Badge variant={status.variant}>{status.label}</Badge>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1 text-sm font-medium ${
                            d.cnhPoints >= 20 ? 'text-destructive' : 'text-muted-foreground'
                          }`}
                        >
                          {d.cnhPoints >= 20 && <BadgeAlert className="size-3.5" />}
                          {d.cnhPoints}
                          <span className="text-xs text-muted-foreground">/40</span>
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="size-8">
                              <MoreHorizontal className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/app/drivers/${d.id}`); }}>
                              <Eye className="size-4" />
                              Ver detalhes
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEdit(d); }}>
                              <Pencil className="size-4" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => { e.stopPropagation(); setDeleteTarget(d); }}
                              className="text-destructive"
                            >
                              <Trash2 className="size-4" />
                              Remover
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {total > 20 && (
            <div className="flex items-center justify-between border-t border-border px-4 py-3 text-xs text-muted-foreground">
              <span>
                Mostrando {(page - 1) * 20 + 1}–{Math.min(page * 20, total)} de {total}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page * 20 >= total}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <DriverFormDialog open={formOpen} onOpenChange={setFormOpen} driver={editing} />

      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Remover motorista</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja remover <strong>{deleteTarget?.name}</strong>? Os dados
              históricos são mantidos, mas ele some das listagens ativas.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              loading={deleteMutation.isPending}
            >
              Remover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
